import { useEffect, useRef, useState, type CSSProperties } from 'react'
import { Box, Text } from '@mantine/core'
import { useMergedRef } from '@mantine/hooks'
import { TbChevronLeft, TbChevronRight } from 'react-icons/tb'
import { useTranslation } from 'react-i18next'

import TooltipIconButton from './TooltipIconButton'
import { copyToCanvas, useFittedSize } from './useFittedCanvas'

type Face = 'front' | 'back'

const arrowStyle = (side: 'left' | 'right'): CSSProperties => ({
  position: 'absolute',
  [side]: 8,
  top: '50%',
  transform: 'translateY(-50%)',
})

const PhotoStage = ({
  image,
  message,
  onPrev,
  onNext,
  prevDisabled,
  nextDisabled,
  onOpen,
  stageRef,
}: {
  image: HTMLCanvasElement | null
  message: string | null
  onPrev: () => void
  onNext: () => void
  prevDisabled: boolean
  nextDisabled: boolean
  onOpen: () => void
  stageRef: (node: HTMLDivElement | null) => void
}) => {
  const { t } = useTranslation('documentation')
  const rootRef = useRef<HTMLDivElement>(null)
  const mergedRef = useMergedRef(rootRef, stageRef)
  const frontRef = useRef<HTMLCanvasElement>(null)
  const backRef = useRef<HTMLCanvasElement>(null)
  const canvases = { front: frontRef, back: backRef }
  const visible = useRef<Face>('front')
  const [shown, setShown] = useState<Face>('front')
  const fitted = useFittedSize(image, rootRef)

  useEffect(() => {
    if (!image) return
    const next: Face = visible.current === 'front' ? 'back' : 'front'
    const canvas = (next === 'front' ? frontRef : backRef).current
    if (!canvas) return
    copyToCanvas(canvas, image)
    visible.current = next
    setShown(next)
  }, [image])

  const canvasProps = (face: Face) => ({
    ref: canvases[face],
    'data-testid': image && shown === face ? 'documentation-stage-image' : undefined,
    onClick: onOpen,
    style: {
      display: image && shown === face ? 'block' : 'none',
      cursor: 'zoom-in',
      width: fitted ? `${fitted.width}px` : undefined,
      height: fitted ? `${fitted.height}px` : undefined,
    },
  })

  return (
    <Box
      ref={mergedRef}
      pos="relative"
      h="100%"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flex: 1,
        width: '100%',
        minWidth: 0,
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      <canvas {...canvasProps('front')} />
      <canvas {...canvasProps('back')} />
      {message ? (
        <Text c="dimmed" size="md" ta="center" px="lg">
          {message}
        </Text>
      ) : null}
      <TooltipIconButton
        label={t('previous')}
        size="xl"
        position="right"
        style={arrowStyle('left')}
        disabled={prevDisabled}
        onClick={onPrev}
      >
        <TbChevronLeft />
      </TooltipIconButton>
      <TooltipIconButton
        label={t('next')}
        size="xl"
        position="left"
        style={arrowStyle('right')}
        disabled={nextDisabled}
        onClick={onNext}
      >
        <TbChevronRight />
      </TooltipIconButton>
    </Box>
  )
}

export default PhotoStage
